import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, Loader2 } from 'lucide-react';

export const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [role, setRole] = useState('customer');
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const user = await login(formData.email, formData.password);
        toast.success('Welcome back!');
        navigate(user.role === 'contractor' ? '/contractor/dashboard' : '/customer/dashboard');
      } else {
        const user = await register({ ...formData, role });
        toast.success('Account created successfully!');
        navigate(user.role === 'contractor' ? '/contractor/dashboard' : '/customer/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-950 flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 lg:px-16 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-stone-400 hover:text-white mb-8 w-fit transition-colors">
          <ArrowLeft size={20} />
          <span>Back to Home</span>
        </Link>

        <div className="max-w-md w-full">
          <div className="mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_clearing-crew/artifacts/qizuc7gv_file_00000000d05471f59cc81c7b9868657c.png" 
              alt="IronLink" 
              className="h-20 mb-6"
            />
            <h1 className="font-heading text-4xl text-white uppercase tracking-tight">
              {isLogin ? 'Welcome Back' : 'Join IronLink'}
            </h1>
            <p className="text-stone-400 mt-2">
              {isLogin ? 'Sign in to continue' : 'Create your account to get started'}
            </p>
          </div>

          <Tabs value={isLogin ? 'login' : 'register'} onValueChange={(v) => setIsLogin(v === 'login')}>
            <TabsList className="grid w-full grid-cols-2 bg-stone-900 border border-stone-700">
              <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Create Account</TabsTrigger>
            </TabsList>

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <TabsContent value="register" className="space-y-4 mt-0">
                <div>
                  <Label className="form-label">I am a</Label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    <button
                      type="button"
                      onClick={() => setRole('customer')}
                      data-testid="role-customer"
                      className={`p-4 border rounded-sm text-left transition-all ${
                        role === 'customer' 
                          ? 'border-orange-500 bg-orange-500/10 text-white' 
                          : 'border-stone-700 bg-stone-900 text-stone-400 hover:border-stone-600'
                      }`}
                    >
                      <span className="block font-heading text-lg uppercase">Landowner</span>
                      <span className="text-sm opacity-70">Post jobs, hire contractors</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setRole('contractor')}
                      data-testid="role-contractor"
                      className={`p-4 border rounded-sm text-left transition-all ${
                        role === 'contractor' 
                          ? 'border-orange-500 bg-orange-500/10 text-white' 
                          : 'border-stone-700 bg-stone-900 text-stone-400 hover:border-stone-600'
                      }`}
                    >
                      <span className="block font-heading text-lg uppercase">Contractor</span>
                      <span className="text-sm opacity-70">Find work, submit bids</span>
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="name" className="form-label">Full Name</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    className="mt-2 bg-stone-900 border-stone-700 h-12"
                    data-testid="register-name"
                    required={!isLogin}
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="form-label">Phone (Optional)</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    className="mt-2 bg-stone-900 border-stone-700 h-12"
                    data-testid="register-phone"
                  />
                </div>
              </TabsContent>

              <div>
                <Label htmlFor="email" className="form-label">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="mt-2 bg-stone-900 border-stone-700 h-12"
                  data-testid="auth-email"
                  required
                />
              </div>

              <div>
                <Label htmlFor="password" className="form-label">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="mt-2 bg-stone-900 border-stone-700 h-12"
                  data-testid="auth-password"
                  required
                />
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                data-testid="auth-submit"
                className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-heading uppercase tracking-wide text-lg rounded-sm"
              >
                {loading ? (
                  <Loader2 className="animate-spin" />
                ) : isLogin ? (
                  'Sign In'
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>
          </Tabs>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:flex-1 relative">
        <div className="absolute inset-0 bg-gradient-to-l from-transparent to-stone-950 z-10" />
        <img
          src="https://images.unsplash.com/photo-1763515186653-2df0fba44697?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxleGNhdmF0b3IlMjBsYW5kJTIwY2xlYXJpbmclMjBoZWF2eSUyMG1hY2hpbmVyeXxlbnwwfHx8fDE3NzA0NDk3MzR8MA&ixlib=rb-4.1.0&q=85"
          alt="Heavy machinery"
          className="w-full h-full object-cover"
        />
      </div>
    </div>
  );
};
